import type { AgnesImageSize, CropArea, CropMode, PreparedImage, SizeChoice } from "./types";

const SUPPORTED_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);
const DATA_URI_RE = /^data:(image\/(?:png|jpeg|webp));base64,([a-z0-9+/=\s]+)$/i;

export const MAX_SOURCE_FILE_BYTES = 12 * 1024 * 1024;
export const MAX_DATA_URI_BYTES = 6 * 1024 * 1024;

export interface PrepareImageOptions {
  rotation?: number;
  cropArea?: CropArea;
  cropMode?: CropMode;
  targetAspect?: number;
  maxDimension?: number;
  quality?: number;
}

export function assertSupportedImage(file: File): void {
  if (!SUPPORTED_TYPES.has(file.type)) {
    throw new Error("请上传 JPG、PNG 或 WebP 图片。");
  }
  if (file.size > MAX_SOURCE_FILE_BYTES) {
    throw new Error("图片过大，请选择 12MB 以内的照片。");
  }
}

export function dataUriBytes(dataUri: string): number {
  const match = dataUri.match(DATA_URI_RE);
  if (!match) return 0;
  const base64 = match[2].replace(/\s/g, "");
  const padding = base64.endsWith("==") ? 2 : base64.endsWith("=") ? 1 : 0;
  return Math.max(0, Math.floor((base64.length * 3) / 4) - padding);
}

export function dataUriMime(dataUri: string): string | null {
  return dataUri.match(DATA_URI_RE)?.[1].toLowerCase() ?? null;
}

export function validateImageDataUri(dataUri: string, maxBytes = MAX_DATA_URI_BYTES): void {
  const mimeType = dataUriMime(dataUri);
  if (!mimeType || !SUPPORTED_TYPES.has(mimeType)) {
    throw new Error("图片格式无效。");
  }
  if (dataUriBytes(dataUri) > maxBytes) {
    throw new Error("压缩后的图片仍然过大，请尝试裁剪后再上传。");
  }
}

export function resolveOutputSize(choice: SizeChoice, width: number, height: number): AgnesImageSize {
  if (choice !== "auto") return choice;

  const ratio = width / Math.max(1, height);
  if (ratio > 1.12) return "1024x768";
  if (ratio < 0.89) return "768x1024";
  return "1024x1024";
}

export function sizeToAspect(size: AgnesImageSize): number {
  const [width, height] = size.split("x").map(Number);
  return width / height;
}

export async function prepareImage(file: File, options: PrepareImageOptions = {}): Promise<PreparedImage> {
  assertSupportedImage(file);

  const sourceDataUri = await readFileAsDataUri(file);
  const img = await loadImage(sourceDataUri);
  const rotation = normalizeRotation(options.rotation ?? 0);
  const maxDimension = options.maxDimension ?? 1440;
  const quality = options.quality ?? 0.86;

  const rotated = drawRotatedImage(img, rotation);
  const baseRect = getSourceRect(
    rotated.width,
    rotated.height,
    options.cropMode ?? "contain",
    options.targetAspect
  );
  const sourceRect = options.cropArea ? getCropAreaRect(baseRect, options.cropArea) : baseRect;
  const { width, height } = fitWithin(sourceRect.width, sourceRect.height, maxDimension);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = getContext(canvas);
  ctx.drawImage(
    rotated.canvas,
    sourceRect.x,
    sourceRect.y,
    sourceRect.width,
    sourceRect.height,
    0,
    0,
    width,
    height
  );

  const dataUri = await canvasToDataUri(canvas, "image/jpeg", quality);
  validateImageDataUri(dataUri);

  return {
    dataUri,
    fileName: file.name,
    width,
    height,
    mimeType: "image/jpeg",
    bytes: dataUriBytes(dataUri),
    rotation
  };
}

function normalizeRotation(rotation: number): number {
  return ((Math.round(rotation / 90) * 90) % 360 + 360) % 360;
}

function readFileAsDataUri(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("读取图片失败。"));
    reader.readAsDataURL(file);
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("图片解码失败。"));
    img.src = src;
  });
}

function drawRotatedImage(img: HTMLImageElement, rotation: number) {
  const quarterTurn = rotation === 90 || rotation === 270;
  const canvas = document.createElement("canvas");
  canvas.width = quarterTurn ? img.naturalHeight : img.naturalWidth;
  canvas.height = quarterTurn ? img.naturalWidth : img.naturalHeight;
  const ctx = getContext(canvas);

  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);

  return { canvas, width: canvas.width, height: canvas.height };
}

function getSourceRect(width: number, height: number, cropMode: CropMode, targetAspect?: number) {
  if (cropMode !== "cover" || !targetAspect || targetAspect <= 0) {
    return { x: 0, y: 0, width, height };
  }

  const currentAspect = width / height;
  if (currentAspect > targetAspect) {
    const croppedWidth = Math.round(height * targetAspect);
    return { x: Math.round((width - croppedWidth) / 2), y: 0, width: croppedWidth, height };
  }

  const croppedHeight = Math.round(width / targetAspect);
  return { x: 0, y: Math.round((height - croppedHeight) / 2), width, height: croppedHeight };
}

function getCropAreaRect(baseRect: SourceRect, cropArea: CropArea) {
  const area = normalizeCropArea(cropArea);
  return {
    x: baseRect.x + Math.round(area.x * baseRect.width),
    y: baseRect.y + Math.round(area.y * baseRect.height),
    width: Math.max(1, Math.round(area.width * baseRect.width)),
    height: Math.max(1, Math.round(area.height * baseRect.height))
  };
}

export function normalizeCropArea(cropArea: CropArea): CropArea {
  const minSize = 0.08;
  const width = clamp(cropArea.width, minSize, 1);
  const height = clamp(cropArea.height, minSize, 1);
  return {
    x: clamp(cropArea.x, 0, 1 - width),
    y: clamp(cropArea.y, 0, 1 - height),
    width,
    height
  };
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, Number.isFinite(value) ? value : min));
}

function fitWithin(width: number, height: number, maxDimension: number) {
  const scale = Math.min(1, maxDimension / Math.max(width, height));
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale))
  };
}

type SourceRect = ReturnType<typeof getSourceRect>;

function getContext(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
  const ctx = canvas.getContext("2d", { alpha: false });
  if (!ctx) throw new Error("浏览器不支持图片处理。");
  return ctx;
}

function canvasToDataUri(canvas: HTMLCanvasElement, mimeType: string, quality: number): Promise<string> {
  if (!canvas.toBlob) {
    return Promise.resolve(canvas.toDataURL(mimeType, quality));
  }

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("图片压缩失败。"));
          return;
        }
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(new Error("图片压缩失败。"));
        reader.readAsDataURL(blob);
      },
      mimeType,
      quality
    );
  });
}
